import { useCallback, useEffect, useState } from "react";
import { SelectV2Value, SelectV2Option } from "../SelectV2.types.js";

type UseSelectMenuProps<Value extends SelectV2Value> = {
    options: SelectV2Option<Value>[];
    multiple: boolean;
};

export const useSelectMenu = <Value extends SelectV2Value>({ options, multiple }: UseSelectMenuProps<Value>) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const [search, setSearch] = useState<string | undefined>();

    // Reset search when menu closes (only for single select)
    useEffect(() => {
        if (!isMenuOpen && !multiple) setSearch(undefined);
    }, [isMenuOpen, multiple]);

    // Keep menu open when searching in multiple mode
    useEffect(() => {
        if (multiple && !isMenuOpen && search) setIsMenuOpen(true);
    }, [multiple, isMenuOpen, search]);

    const openMenu = useCallback(() => {
        setIsMenuOpen(true);
    }, []);

    const closeMenu = useCallback(() => {
        setIsMenuOpen(false);
        if (!multiple) setSearch(undefined);
    }, [multiple]);

    const handleSearchChange = useCallback((newSearch: string) => {
        setSearch(newSearch);
    }, []);

    // Filter options based on search
    const filteredOptions = useCallback(() => {
        if (!search) return options;

        return options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase().trim()));
    }, [options, search]);

    return {
        isMenuOpen,
        search,
        filteredOptions: filteredOptions(),
        openMenu,
        closeMenu,
        handleSearchChange,
    };
};
